import type { Transition, Variants } from 'motion/react';

/**
 * Motion vocabulary.
 *
 * Springs rather than durations, everywhere. A duration-eased element moves at a
 * speed nothing in the physical world moves at, and the eye reads it as
 * mechanical even when it cannot say why. A spring has mass and settles, which
 * is why it feels like a thing rather than a tween.
 *
 * These are shared so the whole interface settles at the same rate. Motion that
 * varies per component reads as sloppiness, not as variety.
 */

/** Cards, panels, anything with visual weight. Settles without overshoot. */
export const springSoft: Transition = { type: 'spring', stiffness: 260, damping: 30, mass: 0.9 };

/** Buttons, chips, small controls. Faster, with a touch of overshoot. */
export const springSnappy: Transition = { type: 'spring', stiffness: 420, damping: 26, mass: 0.6 };

/** Layout and shared-element transitions. Heavier, so large moves stay legible. */
export const springLayout: Transition = { type: 'spring', stiffness: 200, damping: 32, mass: 1.1 };

/**
 * Staggered rise. The 40ms gap is deliberate: below ~25ms the group reads as one
 * block, above ~70ms the user starts waiting for the last item.
 */
export const riseContainer: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.04, delayChildren: 0.06 },
  },
};

export const riseItem: Variants = {
  hidden: { opacity: 0, y: 14, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springSoft,
  },
};

/** Panels arriving from the edge — drawers, inspectors, command palettes. */
export const slideFromRight: Variants = {
  hidden: { opacity: 0, x: 28 },
  show: { opacity: 1, x: 0, transition: springSoft },
  exit: { opacity: 0, x: 28, transition: { duration: 0.16 } },
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.96 },
  show: { opacity: 1, scale: 1, transition: springSnappy },
  exit: { opacity: 0, scale: 0.98, transition: { duration: 0.14 } },
};

/**
 * Hover lift. Two pixels — enough to register, little enough that a grid of
 * cards does not appear to twitch as the cursor crosses it.
 */
export const hoverLift = {
  whileHover: { y: -2, transition: springSnappy },
  whileTap: { scale: 0.995, transition: springSnappy },
} as const;
